import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import api from "../../../../../services/api";
import { NetworkRepEntry } from "../../../../network/@types/Url";
import networkApi from "../../../../network/api";
import useBulkNetworkCategories, {
  withNetworkCategories
} from "../../../../network/hooks/useBulkNetworkCategories";
import { USEQUERY_DISABLE_REFETCH_OPTIONS } from "../../../../network/consts";
import { chunkData } from "../../../../network/util";
import { useSampleDetailsContext } from "../../../contexts/SampleDetailsContext";
import {
  useGetInterestingStrings,
  useLocalSampleDetails,
  useLocalSampleSummaryParams,
  useGetMediaAddress
} from "../../../hooks";
import {
  MALWARE_CONFIG_ANNOTATION,
  matchesConfigC2,
  useSpectraCoreMalwareConfig,
  withMalwareConfigAnnotation
} from "../../malware_config";
import { useGetMergedRlcsReport } from "../../rlcloudsandbox/hooks/useGetMergedRlcsReport";
import { filterUrlFromSources } from "../../sources/sourcesUtils";
import {
  FieldSelector,
  NetworkReferencesStats,
  NetworkRepWithSource,
  NormalizedNetworkRef
} from "../@types";
import {
  ANALYSIS,
  INITIAL_STATS,
  MEDIAADDRESS_FORMAT,
  SORT_ORDER
} from "../consts";

export const INITIAL_NETWORK_REFERENCES_LIMIT = 1000;
export const NETWORK_REFERENCES_INCREMENT = 1000;
const NETWORK_REFERENCES_CHUNK_LIMIT = 100;

const normalizeData = <T>(
  data: T[],
  fieldSelector: FieldSelector<T>
): NormalizedNetworkRef[] => data?.map(item => fieldSelector(item as T)) || [];

export const sortNetworkReputationData = <T extends NetworkRepEntry>(
  a: T,
  b: T
) => {
  const orderA = SORT_ORDER[a.classification] || Infinity;
  const orderB = SORT_ORDER[b.classification] || Infinity;
  return orderA - orderB;
};

export const sortNetworkReputationDataWithSource = (
  a: NetworkRepWithSource,
  b: NetworkRepWithSource
) => {
  const classificationOrder = sortNetworkReputationData(a, b);
  if (classificationOrder !== 0) return classificationOrder;

  const aHasMediaAddress = a.extra?.some(
    e => e.source === ANALYSIS.MEDIAADDRESS
  )
    ? 0
    : 1;
  const bHasMediaAddress = b.extra?.some(
    e => e.source === ANALYSIS.MEDIAADDRESS
  )
    ? 0
    : 1;
  return aHasMediaAddress - bHasMediaAddress;
};

const len = <T>(item: T[] | undefined) => item?.length || 0;

const getAnalysisNetworkReferencesCount = (
  data: NetworkRepWithSource[],
  analysis: string
) =>
  data.reduce(
    (count, { classification, extra }) => {
      const increment = extra?.find(({ source }) => source === analysis)
        ? 1
        : 0;
      return {
        ...count,
        [classification]: count[classification] + increment,
        total: count.total + increment
      };
    },
    {
      malicious: 0,
      suspicious: 0,
      goodware: 0,
      unknown: 0,
      total: 0
    } as Record<string, number>
  );

type Response = {
  data: Array<NetworkRepWithSource>;
  stats: NetworkReferencesStats;
  dataReady: boolean;
  loadMoreNetworkReferences: (size?: number | undefined) => void;
};

/**
 * This hook fetches network data from multiple sources, and sends it to 0407 to enrich the
 * data with classification.
 *
 * Details:
 *
 * Source data is taken from: RLCS merged report, Sandboxes, Sources and Interesting strings
 * Each source of data is first normalized, then merged into a single array ordered by priority.
 * Merged data is then chunked (by 100 items) sent to 0407 in a bulk of 100 (max urls in 1 request).
 *
 */
const useNetworkReferences = (): Response => {
  const {
    hash = "",
    sourceId,
    relationshipId,
    droppedSourceId,
    parentHash
  } = useLocalSampleSummaryParams();

  const {
    networkReferences: {
      networkReferencesCountCtx,
      increaseNetworkReferencesCountCtx
    }
  } = useSampleDetailsContext();

  const { mergedReportData: rlcsData, isFetched: isFetchedMergedReport } =
    useGetMergedRlcsReport(hash, false);

  const { data: sampleDetails, isFetched: isFetchedSampleDetails } =
    useLocalSampleDetails(
      hash,
      sourceId,
      relationshipId,
      droppedSourceId,
      parentHash
    );

  const { data: sourcesData, isFetched: isFetchedSources } = useQuery({
    queryKey: ["sourcesData", hash],
    queryFn: () => api.sourcesData(hash),
    select: ({ data }) => data,
    enabled: Boolean(hash),
    ...USEQUERY_DISABLE_REFETCH_OPTIONS
  });

  const { data: interestingStrings, isFetched: isFetchedInterestingStrings } =
    useGetInterestingStrings(hash, Number(sourceId), Number(relationshipId));

  const { data: mediaAddress, isFetched: isFetchedMediaAddress } =
    useGetMediaAddress(hash);

  const { c2: spectraCoreC2, c2Urls: spectraCoreC2Urls } =
    useSpectraCoreMalwareConfig(hash);

  const totalCounts = useMemo(() => {
    const {
      rlaa = [],
      cape = [],
      cisco_sma: ciscoSMA = [],
      cuckoo = [],
      fireeye = [],
      joe = [],
      vmray = []
    } = sampleDetails?.sandbox_network_data || {};
    return {
      [ANALYSIS.RLCS]:
        len(rlcsData?.network?.url) +
        len(rlcsData?.network?.tcp) +
        len(rlcsData?.network?.udp) +
        len(rlcsData?.network?.dns) +
        len(rlcsData?.network?.http),
      [ANALYSIS.RLAA]: len(rlaa),
      [ANALYSIS.CAPE]: len(cape),
      [ANALYSIS.CISCO_SMA]: len(ciscoSMA),
      [ANALYSIS.CUCKOO]: len(cuckoo),
      [ANALYSIS.FIREEYE]: len(fireeye),
      [ANALYSIS.JOE]: len(joe),
      [ANALYSIS.MEDIAADDRESS]: len(mediaAddress?.results),
      [ANALYSIS.SOURCES]: len(filterUrlFromSources(sourcesData?.sources)),
      [ANALYSIS.TICORE]: interestingStrings?.count ?? 0,
      [ANALYSIS.VMRAY]: len(vmray)
    };
  }, [
    rlcsData,
    sampleDetails,
    interestingStrings?.count,
    sourcesData?.sources,
    mediaAddress
  ]);

  const rlcsUrlsNormalized = useMemo(
    () => [
      ...normalizeData(rlcsData?.network?.url || [], item => ({
        source: ANALYSIS.RLCS,
        url: item.url,
        protocol: "URL",
        displayName: ANALYSIS.RLCS,
        annotations: item.annotations
      })),
      ...normalizeData(rlcsData?.network?.http || [], item => ({
        source: ANALYSIS.RLCS,
        url: item.url,
        protocol: "HTTP",
        displayName: ANALYSIS.RLCS,
        annotations: item.annotations
      }))
    ],
    [rlcsData]
  );

  const rlcsDomainsNormalized = useMemo(
    () =>
      normalizeData(rlcsData?.network?.dns || [], item => ({
        source: ANALYSIS.RLCS,
        url: item.value,
        protocol: "DNS",
        displayName: ANALYSIS.RLCS,
        annotations: item.annotations
      })),
    [rlcsData]
  );

  const rlcsIpsNormalized = useMemo(
    () => [
      ...normalizeData(rlcsData?.network?.tcp || [], item => ({
        source: ANALYSIS.RLCS,
        url: item.destination_ip,
        protocol: "TCP",
        displayName: ANALYSIS.RLCS,
        annotations: item.annotations
      })),
      ...normalizeData(rlcsData?.network?.udp || [], item => ({
        source: ANALYSIS.RLCS,
        url: item.destination_ip,
        protocol: "UDP",
        displayName: ANALYSIS.RLCS,
        annotations: item.annotations
      }))
    ],
    [rlcsData]
  );

  const sandboxesNormalized = useMemo(() => {
    const {
      rlaa = [],
      cape = [],
      cisco_sma: ciscoSMA = [],
      cuckoo = [],
      fireeye = [],
      joe = [],
      vmray = []
    } = sampleDetails?.sandbox_network_data || {};

    const urls: NormalizedNetworkRef[] = [];
    const ips: NormalizedNetworkRef[] = [];

    [
      { source: ANALYSIS.RLAA, data: rlaa },
      { source: ANALYSIS.CAPE, data: cape },
      {
        source: ANALYSIS.CISCO_SMA,
        data: ciscoSMA
      },
      { source: ANALYSIS.CUCKOO, data: cuckoo },
      {
        source: ANALYSIS.FIREEYE,
        data: fireeye
      },
      { source: ANALYSIS.JOE, data: joe },
      { source: ANALYSIS.VMRAY, data: vmray }
    ].forEach(({ source, data }) => {
      data?.forEach(item => {
        if (item.ip) {
          ips.push({
            source,
            url: item.ip,
            protocol: item.protocol.toUpperCase(),
            displayName: source
          });
        } else if (item.uri) {
          urls.push({
            source,
            url: item.uri,
            protocol: item.protocol.toUpperCase(),
            displayName: source
          });
        } else if (item.domain) {
          urls.push({
            source,
            url: item.domain,
            protocol: item.protocol.toUpperCase(),
            displayName: source
          });
        }
      });
    });

    return {
      urls,
      ips
    };
  }, [sampleDetails]);

  const sourcesNormalized = useMemo(
    () =>
      normalizeData(filterUrlFromSources(sourcesData?.sources), item => ({
        source: ANALYSIS.SOURCES,
        url: item.value,
        protocol: "",
        displayName: ANALYSIS.SOURCES
      })),
    [sourcesData]
  );

  const interestingStringsNormalized = useMemo(
    () =>
      Object.values(interestingStrings?.strings ?? {})
        .flatMap(files => files)
        .map(value => ({ ...value, displayName: value.source })),
    [interestingStrings?.strings]
  ) as unknown as NormalizedNetworkRef[];

  const mediaAddressNormalized = useMemo(
    () =>
      normalizeData(mediaAddress?.results || [], item => ({
        source: ANALYSIS.MEDIAADDRESS,
        url: item.value,
        filename: item.filename,
        relationship_id: item.relationship_id,
        sha1: item.sha1,
        displayName: `${
          item.format === MEDIAADDRESS_FORMAT.QR ? "QR" : "Image"
        } (${ANALYSIS.MEDIAADDRESS})`
      })),
    [mediaAddress]
  );

  const isAllFetched =
    isFetchedMergedReport &&
    isFetchedInterestingStrings &&
    isFetchedSampleDetails &&
    isFetchedSources &&
    isFetchedMediaAddress;

  const mappings = useMemo(() => {
    const mapping: Record<string, Array<NormalizedNetworkRef>> = {};
    if (!isAllFetched) return mapping;

    [
      ...rlcsUrlsNormalized,
      ...sandboxesNormalized.urls,
      ...sourcesNormalized,
      ...interestingStringsNormalized,
      ...rlcsDomainsNormalized,
      ...rlcsIpsNormalized,
      ...sandboxesNormalized.ips,
      ...mediaAddressNormalized
    ].forEach(item => {
      if (mapping[item.url]) {
        mapping[item.url]?.push(item);
      } else {
        mapping[item.url] = [item];
      }
    });

    return mapping;
  }, [
    isAllFetched,
    interestingStringsNormalized,
    rlcsDomainsNormalized,
    rlcsIpsNormalized,
    rlcsUrlsNormalized,
    sandboxesNormalized,
    sourcesNormalized,
    mediaAddressNormalized
  ]);

  const chunks = useMemo(
    () =>
      chunkData(
        Object.keys(mappings).slice(0, networkReferencesCountCtx),
        NETWORK_REFERENCES_CHUNK_LIMIT
      ),
    [mappings, networkReferencesCountCtx]
  );

  const result = useQueries({
    queries: chunks?.map((chunk, index) => ({
      queryKey: ["networkReputationMerged", hash, index, chunk.length],
      queryFn: () => networkApi.urlReputation(chunk).then(resp => resp.data),
      enabled: isAllFetched && Boolean(chunks.length),
      ...USEQUERY_DISABLE_REFETCH_OPTIONS
    })),
    combine: results => {
      const isAllFinished = results.every(({ isFetched }) => isFetched);
      if (!results.length || !isAllFinished)
        return {
          stats: INITIAL_STATS,
          enrichedData: [],
          queries: results
        };

      const enrichedData = results
        ?.flatMap(({ data }) => data?.entries ?? [])
        .map(item => ({
          ...item,
          extra:
            mappings[item?.requested_network_location] ??
            mappings[
              item?.requested_network_location?.replace(/^https?:\/\//, "")
            ] ??
            []
        }))
        .map(item =>
          withMalwareConfigAnnotation(
            item,
            MALWARE_CONFIG_ANNOTATION.C2,
            matchesConfigC2(
              spectraCoreC2,
              spectraCoreC2Urls,
              item?.requested_network_location
            )
          )
        )
        .sort(sortNetworkReputationDataWithSource);

      const stats = Object.entries(totalCounts).reduce(
        (acc, [analysisKey, analysisTotal]) => ({
          ...acc,
          total: acc.total + analysisTotal,
          hasMoreData: acc.total + analysisTotal > networkReferencesCountCtx,
          [analysisKey]: getAnalysisNetworkReferencesCount(
            enrichedData,
            analysisKey
          )
        }),
        { total: 0, hasMoreData: false }
      ) as NetworkReferencesStats;

      return {
        queries: results,
        stats,
        enrichedData
      };
    }
  });

  const { categoriesByLocation, isLoading: isLoadingCategories } =
    useBulkNetworkCategories(result.enrichedData);

  const dataWithCategories = useMemo(
    () =>
      withNetworkCategories(
        result.enrichedData,
        categoriesByLocation,
        isLoadingCategories
      ),
    [result.enrichedData, categoriesByLocation, isLoadingCategories]
  );

  // If all queries are fetched, and all data is fetched, then data is ready. If there are no queries, data is ready.

  const dataReady =
    result?.queries?.every(({ isFetched }) => isFetched) ||
    !result?.queries?.length;

  return {
    data: dataWithCategories,
    stats: result.stats,
    dataReady: dataReady && isAllFetched,
    loadMoreNetworkReferences: increaseNetworkReferencesCountCtx
  };
};

export default useNetworkReferences;
